'use client';
/** @jsxImportSource @emotion/react */

import { type ElementType, forwardRef, type Ref } from 'react';

import type {
  DividerPropsVariantOverrides,
  DividerTypeMap as MuiDividerTypeMap,
} from '@mui/material/Divider';
import type {
  OverridableComponent,
  OverrideProps,
} from '@mui/material/OverridableComponent';
import type { OverridableStringUnion } from '@mui/types';

import MuiDivider from '@mui/material/Divider';

export { dividerClasses } from '@mui/material/Divider';

type ExcludedMuiProps = 'children' | 'light' | 'textAlign' | 'variant';

type CustomProps = {
  /**
   * The variant to use.
   * @default 'fullWidth'
   */
  variant?: OverridableStringUnion<
    'fullWidth' | 'inset', // @deprecated 'middle'
    DividerPropsVariantOverrides
  >;
  component?: ElementType;
};

type DividerTypeMap<
  P = CustomProps,
  D extends ElementType = MuiDividerTypeMap['defaultComponent'],
> = {
  defaultComponent: D;
  props: Omit<MuiDividerTypeMap['props'], ExcludedMuiProps> & P;
};

/**
 * @deprecated Please use `Divider` from `@zeus/core/next` instead.
 * @see {@link https://zeus.serko.travel/?path=/docs/zeus-core-next-components-divider-migration--divider-migration-guide Migration guide}
 */
export type DividerProps<
  D extends ElementType = MuiDividerTypeMap['defaultComponent'],
  P = CustomProps,
> = OverrideProps<MuiDividerTypeMap<P, D>, D>;

const DividerComponent = (props: DividerProps, ref: Ref<HTMLHRElement>) => {
  return <MuiDivider {...props} ref={ref} />;
};

DividerComponent.displayName = 'Divider';

/**
 * @deprecated Please use `Divider` from `@zeus/core/next` instead.
 * @see {@link https://zeus.serko.travel/?path=/docs/zeus-core-next-components-divider-migration--divider-migration-guide Migration guide}
 */
export const Divider = forwardRef(
  DividerComponent
) as OverridableComponent<DividerTypeMap>;
